/**
 * UserRow Component
 * Display user in horizontal row format with checkbox and contextual actions
 */

import type { User } from '../../services/users.service';
import { useAuth } from '../../hooks/useAuth';
import { useMemberImage } from '../../hooks/useMemberImage';
import { getInitials } from '../../lib/utils';
import { hasAdminAccess, isHr, ROLE_LABELS } from '../../lib/roles';
import { USER_STATUS_COLORS } from './userStatusColors';

export interface UserRowProps {
  user: User;
  isSelected: boolean;
  onToggleSelection: () => void;
  onSendInvitation?: () => void;
  onCancelInvitation?: () => void;
  onResendInvitation?: () => void;
  onChangeRole?: () => void;
  onDeactivate?: () => void;
  onReactivate?: () => void;
}

type UserStatus = 'active' | 'invited' | 'invited_expired' | 'uninvited_member' | 'deactivated';

export const UserRow = ({
  user,
  isSelected,
  onToggleSelection,
  onSendInvitation,
  onCancelInvitation,
  onResendInvitation,
  onChangeRole,
  onDeactivate,
  onReactivate,
}: UserRowProps) => {
  const { user: currentUser } = useAuth();

  // Load profile image with automatic retry on blob URL failures
  const { imageUrl: imageBlobUrl, isLoading: imageLoading, handleImageError } = useMemberImage(
    user.member_id ?? null,
    user.has_profile_picture ?? false
  );

  const getUserStatus = (): UserStatus => {
    if (user.record_type === 'uninvited_member') {
      return 'uninvited_member';
    }

    // Check if user is deactivated (highest priority for users with accounts)
    if (user.deactivated_at) {
      return 'deactivated';
    }

    if (user.email_verified_at) {
      return 'active';
    }

    // Check if invitation was revoked (cancelled by admin)
    if (user.invitation_revoked_at) {
      return 'invited_expired';
    }

    if (user.invitation_token) {
      if (user.invitation_expires_at) {
        const now = new Date();
        const expiresAt = new Date(user.invitation_expires_at);
        if (now > expiresAt) {
          return 'invited_expired';
        }
      }
      return 'invited';
    }

    return 'uninvited_member';
  };

  const getStatusConfig = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return {
          text: 'Active',
          icon: (
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="5" />
            </svg>
          ),
        };
      case 'invited':
        return {
          text: 'Invited',
          icon: (
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      case 'invited_expired':
        return {
          text: 'Invited (Expired)',
          icon: (
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      case 'deactivated':
        return {
          text: 'Inactive',
          icon: (
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          ),
        };
      default:
        return {
          text: 'Not Invited',
          icon: (
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="5" />
            </svg>
          ),
        };
    }
  };

  const getInvitationTooltip = (): string => {
    if (!user.invitation_sent_at) return '';

    const sentDate = new Date(user.invitation_sent_at);
    const expiresDate = user.invitation_expires_at ? new Date(user.invitation_expires_at) : null;
    const now = new Date();

    const daysAgo = Math.floor((now.getTime() - sentDate.getTime()) / (1000 * 60 * 60 * 24));
    const sentAgo = daysAgo === 0 ? 'today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;

    if (expiresDate) {
      const daysUntilExpiry = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry > 0) {
        return `Sent ${sentAgo}, expires in ${daysUntilExpiry} day(s)`;
      } else {
        return `Sent ${sentAgo}, expired`;
      }
    }

    return `Sent ${sentAgo}`;
  };

  const status = getUserStatus();
  const statusConfig = getStatusConfig(status);
  const displayName =
    user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email;
  const showInvitationTooltip = status === 'invited' || status === 'invited_expired';
  const showRoleBadge = (status === 'active' || status === 'deactivated') && !!user.role;
  const hasMemberName = !!(user.first_name && user.last_name);
  // Show the email row only when the role badge isn't taking the "supplementary info" slot.
  // Users with a role badge get: name / badge / status (3 rows).
  // Users without a role badge get: name / email / status (3 rows).
  const showEmailRow = hasMemberName && !showRoleBadge;

  // HR PRD §1: only HR managers may act on HR-manager accounts; nobody edits their own role.
  const canManageTarget = user.role !== 'hr' || isHr(currentUser?.role);
  const isSelf =
    currentUser?.id != null && user.user_id != null && Number(user.user_id) === Number(currentUser.id);

  return (
    <div className={`group px-6 py-4 hover:bg-surface-hover transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
      <div className="flex items-center justify-between">
        {/* Left: Checkbox + Profile Image + User Info */}
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          {/* Checkbox */}
          <div className="flex-shrink-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelection}
              aria-label={`Select ${displayName}`}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-strong rounded cursor-pointer"
            />
          </div>

          {/* Profile Image */}
          <div className="flex-shrink-0">
            {user.has_profile_picture && user.member_id && imageBlobUrl ? (
              <div className="w-16 h-16 rounded-full bg-surface-secondary flex items-center justify-center overflow-hidden">
                {imageLoading ? (
                  <svg className="animate-spin h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <img src={imageBlobUrl} alt={displayName} className="w-full h-full object-cover" onError={handleImageError} />
                )}
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-xl font-bold">
                {user.first_name && user.last_name ? getInitials(user.first_name, user.last_name) : (
                  <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col items-start space-y-1 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
              <h3 className="w-full sm:w-auto text-lg font-medium text-heading truncate">{displayName}</h3>
              {showRoleBadge && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'hr'
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                      : user.role === 'admin'
                        ? 'bg-status-accent text-status-accent'
                        : 'bg-status-info text-status-info'
                  }`}
                >
                  {ROLE_LABELS[user.role]}
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-meta space-y-1">
              {showEmailRow && (
                <div className="text-sm text-meta truncate">{user.email}</div>
              )}
              <div className="flex items-center space-x-4">
                <span
                  className={`flex items-center ${USER_STATUS_COLORS[status]} ${showInvitationTooltip ? 'cursor-help' : ''}`}
                  title={showInvitationTooltip ? getInvitationTooltip() : ''}
                >
                  <span className="mr-1">{statusConfig.icon}</span>
                  {statusConfig.text}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Contextual Actions */}
        {hasAdminAccess(currentUser?.role) && (
          <div className="user-actions-container hidden lg:block">
            <div className="user-actions flex items-center space-x-2 opacity-0 translate-x-5 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto">
              {!canManageTarget ? (
                <span
                  className="inline-flex items-center text-xs text-meta cursor-help"
                  title="Only HR managers can manage HR accounts"
                >
                  <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  HR-managed
                </span>
              ) : status === 'uninvited_member' ? (
                <>
                  {/* Send Invitation */}
                  {onSendInvitation && (
                    <button
                      onClick={onSendInvitation}
                      className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      title="Send Invitation"
                    >
                      <svg className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="hidden sm:inline">Send Invitation</span>
                    </button>
                  )}
                </>
              ) : status === 'invited_expired' ? (
                <>
                  {/* Resend Invitation */}
                  {onResendInvitation && (
                    <button
                      onClick={onResendInvitation}
                      className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      title="Resend Invitation"
                    >
                      <svg className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="hidden sm:inline">Resend</span>
                    </button>
                  )}
                </>
              ) : status === 'invited' ? (
                <>
                  {/* Cancel Invitation */}
                  {onCancelInvitation && (
                    <button
                      onClick={onCancelInvitation}
                      className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      title="Cancel Invitation"
                    >
                      <svg className="h-4 w-4 mr-1.5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="hidden sm:inline">Cancel</span>
                    </button>
                  )}
                </>
              ) : status === 'deactivated' ? (
                <>
                  {/* Reactivate Button */}
                  {onReactivate && (
                    <button
                      onClick={onReactivate}
                      className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      title="Reactivate User"
                    >
                      <svg className="h-4 w-4 mr-1.5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="hidden sm:inline">Reactivate</span>
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Active User Actions: Change Role, Deactivate. Nobody changes their own role or deactivates themselves (TW-370). */}
                  {onChangeRole && !isSelf && (
                    <button
                      onClick={onChangeRole}
                      className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      title="Change Role"
                    >
                      <svg className="h-4 w-4 mr-1.5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      <span className="hidden sm:inline">Change Role</span>
                    </button>
                  )}
                  {onDeactivate && !isSelf && (
                    <button
                      onClick={onDeactivate}
                      className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      title="Deactivate User"
                    >
                      <svg className="h-4 w-4 mr-1.5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      <span className="hidden sm:inline">Deactivate</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
